import { IsVisible } from '@zg-rentals/react-use';
import { Box, List, Paragraph, TextButton } from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useSatelliteWebRenterConversationHandler_getMessages } from '@zg-rentals/ts-satellite-client-react-query';
import type { SatelliteWebRenterConversationGetLatestConversationsDto } from '@zg-rentals/ts-satellite-client';
import ConfigConstants from '../../constants/ConfigConstants';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { UpdateConversationStateArgs } from '../../types/UpdateConversationStateArgs';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { AppContext } from '../../contexts/AppContextProvider';
import { isHotpads } from '@zg-rentals/multibrand-support';
import { logError } from '@zg-rentals/log-error';
import { bootstrap } from '../../bootstrap';
import { useWebSocketContext } from '../../contexts/WebSocketContextProvider';
import { WEBSOCKET_DEBUG } from '../../hooks/useWebsocketFeature';
import type { WebSocketMessage } from '../../hooks/useWebSocketConnection';
import { emitConversationItemClickEvent } from '../../constants/clickstreamEvents/conversationItemClick';
import { ConversationItemSkeleton, ConversationListSkeleton } from './skeleton';
import ConversationItemContainer from './components/ConversationItemContainer';
import { useInfiniteConversationList } from '../../hooks/useInfiniteConversationList';

const POLL_INTERVAL_IN_SECONDS = 60;
const { MAX_DAYS_BACK_FOR_CONVERSATIONS } = ConfigConstants;
const POLL_INTERVAL_IN_MS = POLL_INTERVAL_IN_SECONDS * 1000;

const logger = bootstrap.logger.child({ name: 'ConversationList' });

export const getConversationToRedirectToNext = (
  currentConversationId: string,
  conversations: Array<SatelliteWebRenterConversationGetLatestConversationsDto>,
) => {
  let nextConversationId = '';

  if (conversations.length > 1) {
    const currentConversationIndex = conversations.findIndex(
      (conversation) => conversation.conversationId === currentConversationId,
    );

    if (currentConversationIndex !== -1) {
      const nextConversationIndex = (currentConversationIndex + 1) % conversations.length;
      nextConversationId = conversations[nextConversationIndex].conversationId || '';
    } else {
      nextConversationId = conversations[0].conversationId || '';
    }
  }

  return nextConversationId;
};

export type ConversationInConversationList = SatelliteWebRenterConversationGetLatestConversationsDto;

type ConversationListProps = {
  shouldFetchOlderConversations: boolean;
  showMessageListOnMobile(): void;
  updateConversationState(args: UpdateConversationStateArgs): void;
  /** Called when the list has finished loading (initial or after folder change). Used by InboxPage to redirect or show empty state without a separate GraphQL call. */
  onConversationsLoaded?: (
    conversations: Array<SatelliteWebRenterConversationGetLatestConversationsDto>,
    isLoading: boolean,
    error: Error | null,
  ) => void;
};

export type ConversationListRef = {
  refetch: () => Promise<void>;
  getConversations: () => Array<SatelliteWebRenterConversationGetLatestConversationsDto>;
};

const ConversationList = forwardRef<ConversationListRef, ConversationListProps>(function ConversationList(
  { shouldFetchOlderConversations, showMessageListOnMobile, updateConversationState, onConversationsLoaded },
  ref,
) {
  const { conversationId, folder, mobileView, messageJustSentRef } = useContext(InboxPageContext);
  const { brand } = useContext(AppContext);
  const isHotPadsBrand = isHotpads(brand);
  const [daysBack, setDaysBack] = useState<number | null>(
    shouldFetchOlderConversations ? MAX_DAYS_BACK_FOR_CONVERSATIONS : null,
  );
  const [isLoadingOlderConversations, setIsLoadingOlderConversations] = useState(false);

  const queryClient = useQueryClient();
  const fetchLatestMessages = useCallback(() => {
    if (!conversationId) return;
    // Skip refetch when we just sent a message; AddMessage already invalidated getMessages (single refetch).
    if (messageJustSentRef?.current && messageJustSentRef.current.conversationId === conversationId) {
      return;
    }
    void queryClient.refetchQueries({
      queryKey: useSatelliteWebRenterConversationHandler_getMessages.getKey({ conversationId }),
      exact: true,
    });
  }, [conversationId, messageJustSentRef, queryClient]);

  // WebSocket integration
  const { subscribeToMessages, hadErrorSinceLastOpen, isWebSocketEnabledAndReady } = useWebSocketContext();

  const { conversations, hasNextPage, isLoading, isFetchingNextPage, isRefetching, error, refetch, fetchNextPage } =
    useInfiniteConversationList({
      folder,
      daysBack,
      isWebSocketEnabledAndReady,
      pollIntervalMs: POLL_INTERVAL_IN_MS,
    });

  // Clear loading state when data arrives
  useEffect(() => {
    if (isLoadingOlderConversations && !isFetchingNextPage && !isRefetching) {
      setIsLoadingOlderConversations(false);
    }
  }, [isLoadingOlderConversations, isFetchingNextPage, isRefetching]);

  // When conversation list is polled/refetched (and WebSocket is not ready),
  // also fetch latest messages for the current conversation
  const prevIsFetchingRef = useRef(false);
  useEffect(() => {
    // Detect when a refetch completes
    if (prevIsFetchingRef.current && !isRefetching && !isFetchingNextPage) {
      // Only fetch latest messages when WebSocket is not ready (polling fallback behavior)
      if (!isWebSocketEnabledAndReady) {
        fetchLatestMessages();
      }
    }
    prevIsFetchingRef.current = isRefetching || isFetchingNextPage;
  }, [isRefetching, isFetchingNextPage, isWebSocketEnabledAndReady, fetchLatestMessages]);

  const handleWebSocketMessage = useCallback(
    async (message: WebSocketMessage) => {
      if (message.event?.type === 'conversationUpdated' || message.event?.type === 'newConversation') {
        if (WEBSOCKET_DEBUG) {
          logger.info('ConversationList: Refetching due to event type:', { eventType: message.event.type });
        }
        await refetch();
        // Invalidate the getMessages cache for the affected conversation so that switching
        // to it triggers a background refetch with fresh data (the global staleTime would
        // otherwise serve stale cache). Skip the current conversation — MessageList handles
        // that via its own WebSocket subscription.
        const eventConversationId = message.event?.conversationId;
        if (eventConversationId && String(eventConversationId) !== String(conversationId)) {
          void queryClient.invalidateQueries({
            queryKey: useSatelliteWebRenterConversationHandler_getMessages.getKey({
              conversationId: String(eventConversationId),
            }),
            exact: true,
          });
        }
      }
    },
    [refetch, conversationId, queryClient],
  );

  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    return subscribeToMessages(handleWebSocketMessage);
  }, [isWebSocketEnabledAndReady, subscribeToMessages, handleWebSocketMessage]);

  useEffect(() => {
    if (!isWebSocketEnabledAndReady || !hadErrorSinceLastOpen) return;
    refetch();
  }, [isWebSocketEnabledAndReady, hadErrorSinceLastOpen, refetch]);

  const showLoadMore = !hasNextPage && !shouldFetchOlderConversations;
  const shouldShowNoConversationsContent = conversations.length === 0 && !error && !isLoading;
  const shouldShowLoadingSkeleton = isLoading && conversations.length === 0 && !error;

  // Notify parent when list has finished loading (for redirect / empty state without separate GraphQL)
  useEffect(() => {
    onConversationsLoaded?.(conversations, isLoading, error ?? null);
  }, [conversations, isLoading, error, onConversationsLoaded]);

  // Expose refetch and getConversations functions to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      refetch,
      getConversations: () => conversations,
    }),
    [refetch, conversations],
  );

  const handleUpdateConversations = () => {
    return updateConversationState({
      conversationId: conversationId || '',
      hasLoadedOlderConversations: true,
      showConversationList: mobileView === InboxPageMobileView.ConversationList,
    });
  };

  const handleFetchMoreConversations = async () => {
    await fetchNextPage();
  };

  const handleLoadOlderConversationsClick = async () => {
    try {
      logger.info('User clicked to load older conversations, getting latest conversations');
      setIsLoadingOlderConversations(true);
      // Update daysBack to load older conversations
      setDaysBack(MAX_DAYS_BACK_FOR_CONVERSATIONS);

      return updateConversationState({
        hasLoadedOlderConversations: true, // This persists the daysBack variable as 660 when changing folders
        conversationId: conversationId || '',
        shouldChangeRoute: false,
      });
    } catch (err) {
      logError({ error: err, context: 'Error fetching latest conversations on load older conversations click' });
      setIsLoadingOlderConversations(false);
    }
  };

  const handleConversationItemVisibilityChange = (isVisible: boolean) => {
    if (isVisible && !isFetchingNextPage) {
      handleFetchMoreConversations();
    }
  };

  const handleConversationItemClick = useCallback(
    (selectedConversationId: string) => {
      if (selectedConversationId === conversationId) {
        showMessageListOnMobile();
        return;
      }
      emitConversationItemClickEvent();

      if (selectedConversationId) {
        updateConversationState({ conversationId: selectedConversationId, showConversationList: false });
      }
    },
    [conversationId, showMessageListOnMobile, updateConversationState],
  );

  if (shouldShowLoadingSkeleton) return <ConversationListSkeleton />;

  if (error) {
    logError({
      error,
      context: {
        info: 'Error fetching conversations in ConversationList',
      },
    });

    return (
      <Box
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Paragraph>There was an error fetching conversations.</Paragraph>
      </Box>
    );
  }

  // When there are no conversations, show only an empty list in the left column.
  // The full empty state (NoConversationsContent with "Load older", etc.) is shown once in the right column by InboxPage.
  if (shouldShowNoConversationsContent) {
    return (
      <List.Root
        aria-label="conversations"
        data-testid="conversation-list"
        appearance="none"
        css={{ overflowY: 'auto' }}
      />
    );
  }

  return (
    <List.Root
      aria-label="conversations"
      data-testid="conversation-list"
      appearance="none"
      css={{
        overflowY: 'auto',
      }}
    >
      {conversations.map((conversation) => (
        <ConversationItemContainer
          key={conversation.conversationId}
          conversation={conversation}
          onClick={handleConversationItemClick}
        />
      ))}

      {hasNextPage && (
        <IsVisible onChange={handleConversationItemVisibilityChange} options={{ rootMargin: '-100px 0px 0px 0px' }}>
          <ConversationItemSkeleton />
          <ConversationItemSkeleton />
        </IsVisible>
      )}

      {showLoadMore && (
        <TextButton onClick={handleLoadOlderConversationsClick} css={{ paddingBlock: 'default', width: '100%' }}>
          Load older conversations
        </TextButton>
      )}
      {isLoadingOlderConversations && (
        <Paragraph
          textStyle="body-bold"
          css={{
            textAlign: 'center',
            paddingTop: 'default !important',
            paddingBottom: 'default !important',
            borderRight: '1px solid {colors.gray.300}',
            color: isHotPadsBrand ? '#007a70 !important' : '{colors.blue.500}',
          }}
        >
          Loading older conversations...
        </Paragraph>
      )}
    </List.Root>
  );
});

export default ConversationList;
